/**
 * Terms of Service Page
 * European consumer law compliant — EU DSA, Consumer Rights Directive, UCPD
 *
 * ⚠️  Before going live, update:
 *   - COMPANY_LEGAL_NAME, COMPANY_ADDRESS, COMPANY_COUNTRY, GOVERNING_LAW_COUNTRY
 *   - EFFECTIVE_DATE, LAST_UPDATED
 *   - Emails: set VITE_EMAIL_LEGAL and VITE_EMAIL_SUPPORT in apps/web/.env
 */

import { usePageSEO } from "@/lib/usePageSEO"
import { Link } from "react-router-dom"
import { ArrowLeft, FileText } from "lucide-react"
import { GoogleAnalytics } from "@/components/shared/GoogleAnalytics"
import { APP_NAME, GA_ID, EMAIL_LEGAL, EMAIL_SUPPORT, LEGAL_COMPANY_NAME, LEGAL_ADDRESS_ENABLED, LEGAL_ADDRESS } from "@/lib/appConfig"

// ─── Edit these before going live ────────────────────────────────────────────
const COMPANY_LEGAL_NAME = LEGAL_COMPANY_NAME
const COMPANY_ADDRESS = LEGAL_ADDRESS
const GOVERNING_LAW_COUNTRY = "[Your Country]"
const LEGAL_EMAIL = EMAIL_LEGAL
const SUPPORT_EMAIL = EMAIL_SUPPORT
const EFFECTIVE_DATE = "9 March 2026"
const LAST_UPDATED = "10 March 2026"
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: "introduction", title: "Introduction" },
  { id: "acceptance", title: "Acceptance of Terms" },
  { id: "service", title: "The Service" },
  { id: "eligibility", title: "Eligibility" },
  { id: "account", title: "Account Registration" },
  { id: "acceptable-use", title: "Acceptable Use" },
  { id: "user-content", title: "Your Content & Data" },
  { id: "intellectual-property", title: "Intellectual Property" },
  { id: "subscription", title: "Subscription & Payment" },
  { id: "disclaimers", title: "Disclaimers" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "indemnification", title: "Indemnification" },
  { id: "termination", title: "Termination" },
  { id: "governing-law", title: "Governing Law" },
  { id: "disputes", title: "Dispute Resolution" },
  { id: "changes", title: "Changes to Terms" },
  { id: "contact", title: "Contact" },
]

function SectionHeader({ id, title }: { id: string; title: string }) {
  return (
    <h2
      id={id}
      className="scroll-mt-24 text-xl font-bold text-foreground mt-12 mb-4 pb-2 border-b border-border/60"
    >
      {title}
    </h2>
  )
}

function InfoBox({ children, variant = "default" }: { children: React.ReactNode; variant?: "default" | "warning" }) {
  const styles =
    variant === "warning"
      ? "bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200"
      : "bg-primary/5 border-primary/20 text-foreground/80"
  return (
    <div className={`${styles} border rounded-xl p-4 text-sm leading-relaxed my-4`}>
      {children}
    </div>
  )
}

export default function TermsOfServicePage() {
  usePageSEO({
    title: `Terms of Service | ${APP_NAME}`,
    description: `Read the ${APP_NAME} Terms of Service. Understand your rights and obligations when using our property management platform.`,
    path: "/terms-of-service",
  })

  return (
    <div className="min-h-screen bg-background">
      <GoogleAnalytics id={GA_ID} />
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur-md border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-bold text-base tracking-tight shrink-0">
            <img src="/app-icon.png" className="h-7 w-7 object-contain" alt={APP_NAME} />
            <span className="hidden sm:inline">{APP_NAME.toUpperCase()}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-muted/30 border-b border-border/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Legal Document
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">Terms of Service</h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-2xl">
            These Terms of Service govern your access to and use of {APP_NAME}. By using our
            Service, you agree to be bound by these Terms. Please read them carefully.
          </p>
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <p>Effective date: <span className="font-semibold">{EFFECTIVE_DATE}</span></p>
            <p>Last updated: <span className="font-semibold">{LAST_UPDATED}</span></p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-[200px_1fr] gap-12">
        {/* TOC Sidebar */}
        <aside className="hidden md:block">
          <div className="sticky top-20 space-y-0.5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 px-2">
              Contents
            </p>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 px-2 py-1.5 rounded-lg transition-colors leading-snug"
              >
                {s.title}
              </a>
            ))}
            <div className="pt-4 border-t border-border/40 mt-4">
              <Link
                to="/privacy-policy"
                className="block text-sm text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg transition-colors"
              >
                → Privacy Policy
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="text-sm text-foreground/80 leading-relaxed min-w-0">

          {/* ── Introduction ── */}
          <SectionHeader id="introduction" title="1. Introduction" />
          <p className="mb-3">
            Welcome to <strong>{APP_NAME}</strong> (referred to as "we", "us", "our", or "the
            Platform"). {APP_NAME} is a property management Software-as-a-Service platform
            designed to help independent landlords manage their rental properties, tenants,
            leases, finances, and maintenance — all in one place.
          </p>
          <p className="mb-3 text-muted-foreground">
            The Service is the <strong>{APP_NAME}</strong> property management platform, managed and provided by <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-bold">SoulSpark Games</a>
            {LEGAL_ADDRESS_ENABLED && COMPANY_ADDRESS && <>, registered at {COMPANY_ADDRESS}</>}.
          </p>
          <p className="mb-3 text-muted-foreground text-xs leading-relaxed">
            {APP_NAME} is a product and service of <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">SoulSpark Games</a>,
            an independent technology and software company. SoulSpark Games develops digital
            tools and software products, of which {APP_NAME} is one. All contractual obligations
            and liabilities under these Terms are held by {COMPANY_LEGAL_NAME}.
          </p>
          <p className="text-muted-foreground">
            These Terms of Service ("Terms") constitute a legally binding agreement between you
            ("User", "you", "your") and {COMPANY_LEGAL_NAME}. They govern your use of the
            {APP_NAME} website, web application, mobile applications, APIs, and any related
            services (collectively, the "Service").
          </p>

          {/* ── Acceptance ── */}
          <SectionHeader id="acceptance" title="2. Acceptance of Terms" />
          <p className="mb-3">
            By registering an account, accessing, or using the Service, you confirm that:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>You have read, understood, and agree to be bound by these Terms.</li>
            <li>You have read and accept our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.</li>
            <li>You meet the eligibility requirements set out in Section 4.</li>
            <li>You have the legal capacity and authority to enter into this agreement.</li>
          </ul>
          <p className="text-muted-foreground">
            If you do not agree with any part of these Terms, you must not use the Service.
          </p>
          <InfoBox>
            If you are using the Service on behalf of a company or other legal entity, you
            represent that you have the authority to bind that entity to these Terms, and these
            Terms will apply to that entity. References to "you" include that entity.
          </InfoBox>

          {/* ── The Service ── */}
          <SectionHeader id="service" title="3. The Service" />
          <p className="mb-4">
            {APP_NAME} provides independent landlords with a digital platform to:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Manage rental properties, rooms, and unit configurations</li>
            <li>Maintain tenant records and contact information</li>
            <li>Create and track rental lease agreements</li>
            <li>Record and monitor rent payments and financial transactions</li>
            <li>Log, assign, and track property maintenance requests</li>
            <li>Store and organise property-related documents and files</li>
            <li>Receive automated reminders for rent due dates and lease expiry</li>
            <li>View financial summaries and reports</li>
          </ul>
          <p className="mb-3 text-muted-foreground">
            <strong className="text-foreground">Service availability:</strong> We strive to provide
            continuous, reliable access to the Service but do not guarantee 100% uptime. We may
            temporarily suspend the Service for maintenance, updates, or reasons beyond our
            control. We will make reasonable efforts to provide advance notice of planned
            maintenance.
          </p>
          <p className="text-muted-foreground">
            <strong className="text-foreground">Service updates:</strong> We continually develop
            and improve {APP_NAME}. We reserve the right to modify, add, or remove features at
            any time. Where a change substantially reduces the functionality of a paid
            subscription, we aim to provide reasonable notice. Continued use of the Service
            after such changes constitutes acceptance.
          </p>

          {/* ── Eligibility ── */}
          <SectionHeader id="eligibility" title="4. Eligibility" />
          <p className="mb-4">To use the Service, you must:</p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Be at least 18 years of age.</li>
            <li>Own, manage, or rent out one or more properties — whether as an individual personal landlord, a professional or portfolio landlord, a property management company, or any other person with a legitimate interest in managing rental property. The Service is available to both personal and professional property owners.</li>
            <li>Not have been previously suspended or removed from the Service for violation of these Terms.</li>
            <li>Comply with all applicable local, national, and EU laws and regulations relating to landlord obligations, data protection, and anti-money laundering.</li>
          </ul>
          <InfoBox>
            <strong>Geographic scope & platform availability:</strong> {APP_NAME} is primarily
            designed for users in the <strong>United States</strong> and the{" "}
            <strong>European Union / European Economic Area</strong>.
            <br /><br />
            The <strong>web application</strong> (accessible at our website) is available globally.
            Users outside the US and EU may access and use the web application, though compliance
            with applicable local laws in those jurisdictions is the user's sole responsibility,
            and the Service is not warranted to be appropriate or lawful for use in every jurisdiction.
            <br /><br />
            The <strong>mobile application</strong> (iOS and Android) is made available
            exclusively through the US and EU regional App Store and Google Play listings.
            Users in other regions may not be able to download the mobile app through official
            app store channels.
          </InfoBox>
          <InfoBox variant="warning">
            <strong>Important:</strong> By using {APP_NAME} to store tenant personal data, you
            act as a data controller under the GDPR and are responsible for compliance with your
            local data protection laws. Please review our{" "}
            <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>{" "}
            and ensure you inform your tenants about how their data is processed on your behalf.
          </InfoBox>

          {/* ── Account ── */}
          <SectionHeader id="account" title="5. Account Registration & Security" />
          <h3 className="font-semibold text-foreground mb-2">5.1 Registration</h3>
          <p className="mb-4 text-muted-foreground">
            To access the Service, you must create an account using your name, email address,
            and a secure password. You agree to provide accurate, current, and complete
            information during registration and to keep this information updated.
          </p>

          <h3 className="font-semibold text-foreground mb-2">5.2 Account Security</h3>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>You are solely responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You must notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a> if you suspect any unauthorised access to your account.</li>
            <li>You are responsible for all activity that occurs under your account, whether or not you authorised it, to the extent permitted by applicable law.</li>
            <li>Do not share your account credentials with others. Each account is for the use of one individual or business entity.</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">5.3 One Account per User</h3>
          <p className="mb-4 text-muted-foreground">
            You may not create multiple accounts to circumvent plan limits, access restrictions,
            or any other terms of this agreement. Doing so may result in termination of all
            associated accounts.
          </p>

          <h3 className="font-semibold text-foreground mb-2">5.4 Email Verification</h3>
          <p className="mb-4 text-muted-foreground">
            We may require you to verify your email address. Access to certain features may be
            restricted until verification is complete.
          </p>

          {/* ── Acceptable Use ── */}
          <SectionHeader id="acceptable-use" title="6. Acceptable Use Policy" />
          <p className="mb-4">You agree to use the Service only for lawful purposes. You must not:</p>

          <h3 className="font-semibold text-foreground mb-2">6.1 Prohibited Activities</h3>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Use the Service to store, process, or transmit any unlawful, fraudulent, defamatory, discriminatory, or otherwise harmful content.</li>
            <li>Violate any applicable local, national, or international laws or regulations, including landlord-tenant law, anti-discrimination law, GDPR, and AML regulations.</li>
            <li>Use the Service to facilitate illegal housing arrangements, undeclared rental income, or tax evasion.</li>
            <li>Use discriminatory criteria (race, gender, religion, sexual orientation, disability, national origin, etc.) when entering tenant data or making tenancy decisions.</li>
            <li>Upload malware, viruses, or any other malicious code.</li>
            <li>Attempt to gain unauthorised access to any part of the Service, our systems, or other users' data.</li>
            <li>Scrape, crawl, or systematically extract data from the Service using automated tools without our prior written consent.</li>
            <li>Circumvent, disable, or interfere with security-related features of the Service.</li>
            <li>Resell, sublicence, or commercially exploit the Service without our express written permission.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">6.2 Content Standards</h3>
          <p className="mb-4 text-muted-foreground">
            All content you upload to the Service (property photos, documents, notes, etc.) must
            not infringe any third-party intellectual property rights and must comply with
            applicable law. We reserve the right to remove content that violates these standards.
          </p>

          {/* ── User Content ── */}
          <SectionHeader id="user-content" title="7. Your Content & Data" />
          <h3 className="font-semibold text-foreground mb-2">7.1 Ownership</h3>
          <p className="mb-4 text-muted-foreground">
            You retain full ownership of all data, content, and information you input into the
            Service ("Your Content"), including property data, tenant records, financial records,
            and uploaded files. We do not claim any ownership rights over Your Content.
          </p>

          <h3 className="font-semibold text-foreground mb-2">7.2 Licence to Us</h3>
          <p className="mb-4 text-muted-foreground">
            By uploading or inputting Your Content, you grant us a limited, non-exclusive,
            worldwide, royalty-free licence to host, store, transmit, display, and process Your
            Content solely to the extent necessary to operate and provide the Service to you.
            This licence terminates when you delete the content or close your account.
          </p>

          <h3 className="font-semibold text-foreground mb-2">7.3 Data Portability & Export</h3>
          <p className="mb-4 text-muted-foreground">
            You may export your data at any time using the export functions available in the
            Service. We are committed to ensuring you are never locked into our platform.
          </p>

          <h3 className="font-semibold text-foreground mb-2">7.4 Your Responsibility for Tenant Data</h3>
          <p className="mb-4 text-muted-foreground">
            You acknowledge that you are the data controller of any personal data about your
            tenants that you enter into the Service. You are solely responsible for ensuring you
            have a lawful basis under GDPR to collect and process such data, and for providing
            your tenants with appropriate privacy notices about how their data is used.
          </p>

          {/* ── Intellectual Property ── */}
          <SectionHeader id="intellectual-property" title="8. Intellectual Property" />
          <p className="mb-4 text-muted-foreground">
            The Service, including its design, software, features, branding, trade marks, and
            all associated intellectual property, is owned by or licensed to {COMPANY_LEGAL_NAME}.
            Nothing in these Terms transfers any intellectual property rights to you.
          </p>
          <p className="mb-4 text-muted-foreground">
            You are granted a limited, non-exclusive, non-transferable, revocable licence to
            access and use the Service for your own internal business purposes, subject to your
            compliance with these Terms.
          </p>
          <p className="text-muted-foreground">
            You must not copy, modify, distribute, sell, reverse engineer, or create derivative
            works based on the Service or any part of it, except as expressly permitted by these
            Terms or applicable law.
          </p>

          {/* ── Subscription & Payment ── */}
          <SectionHeader id="subscription" title="9. Subscription & Payment" />
          <h3 className="font-semibold text-foreground mb-2">9.1 Free Plan</h3>
          <p className="mb-4 text-muted-foreground">
            {APP_NAME} is currently available free of charge during our growth phase. The free
            plan includes access to core features within published usage limits. We reserve the
            right to introduce paid plans in the future, with reasonable advance notice to
            existing users.
          </p>

          <h3 className="font-semibold text-foreground mb-2">9.2 Payment Processing — Planned Platform-Specific Providers</h3>
          <InfoBox variant="warning">
            <strong>⏳ Paid plans have not yet been released.</strong> {APP_NAME} does not
            currently have an active payment system. No payment data is collected, processed,
            or stored at this time. The providers listed below are disclosed for transparency
            and represent our <em>planned</em> payment infrastructure for when paid plans
            launch in the future. We will notify all registered users with at least 30 days'
            notice before any paid plans take effect.
          </InfoBox>
          <p className="mb-3 text-muted-foreground">
            When paid plans are introduced, payment processing will be handled by trusted
            third-party providers depending on the platform you are using. We will{" "}
            <strong className="text-foreground">never store, log, or have access to your full
            payment card number, bank details, or raw payment credentials</strong>.
          </p>
          <div className="space-y-3 mb-4">
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Web Application — Stripe <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-sm text-muted-foreground mt-1">
                Web subscription payments will be processed by{" "}
                <strong>Stripe, Inc.</strong> (USA). Stripe handles card tokenisation and billing
                directly. Your card details will never be transmitted to or stored on our servers.
                Stripe is PCI-DSS Level 1 certified.{" "}
                <a href="https://stripe.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Stripe Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Android App — Google Play Billing <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-sm text-muted-foreground mt-1">
                Android in-app purchases and subscriptions will be processed by{" "}
                <strong>Google Play Billing</strong> (Google LLC, USA) in accordance
                with Google Play's Developer Distribution Agreement and applicable Payments Terms.
                We will receive only anonymised purchase confirmation and subscription status.{" "}
                <a href="https://policies.google.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Google Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">iOS / iPadOS App — Apple In-App Purchase &amp; Apple Pay <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-sm text-muted-foreground mt-1">
                iOS and iPadOS in-app purchases and subscriptions will be processed by{" "}
                <strong>Apple Inc.</strong> via their In-App Purchase system. Apple Pay may be
                offered as a payment method where supported. We will receive only anonymised
                purchase receipts and subscription status.{" "}
                <a href="https://www.apple.com/legal/privacy/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Apple Privacy Policy →</a>
              </p>
            </div>
            <div className="bg-muted/40 rounded-xl p-4 border border-border/40">
              <p className="font-semibold text-foreground text-sm">Cross-Platform Subscription Management — RevenueCat <span className="ml-2 text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">Not yet active</span></p>
              <p className="text-sm text-muted-foreground mt-1">
                <strong>RevenueCat, Inc.</strong> (USA) will serve as our subscription management
                and entitlement layer across iOS, Android, and the web. RevenueCat tracks
                subscription status using anonymised app user identifiers and purchase receipts.
                RevenueCat does <em>not</em> process payment card data and is not a payment processor.{" "}
                <a href="https://www.revenuecat.com/privacy" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">RevenueCat Privacy Policy →</a>
              </p>
            </div>
          </div>

          <h3 className="font-semibold text-foreground mb-2">9.3 Subscription Terms (When Paid Plans Launch)</h3>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>All prices will be clearly displayed inclusive of applicable VAT (where required) before any purchase.</li>
            <li>Subscriptions will auto-renew at the end of each billing period unless cancelled before the renewal date through the relevant platform (web, Google Play, or App Store).</li>
            <li>You may cancel your subscription at any time. Access to paid features will continue until the end of the paid billing period.</li>
            <li>Refund requests for web subscriptions will be handled in accordance with EU consumer rights law and applicable US state laws. Refunds for in-app purchases on Android or iOS will be subject to Google Play and Apple App Store refund policies respectively.</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">9.4 Right of Withdrawal (EU Consumer Rights Directive)</h3>
          <p className="mb-4 text-muted-foreground">
            If you are a consumer (i.e. you are acting outside your trade, business, craft, or
            profession), EU law provides you a 14-day right of withdrawal from distance contracts.
            However, by starting to use a digital service, you may waive this right where the
            service has begun at your explicit request. Full withdrawal details will be provided
            at the point of subscription when paid plans are introduced.
          </p>

          <h3 className="font-semibold text-foreground mb-2">9.5 Price Changes</h3>
          <p className="text-muted-foreground">
            We will give at least 30 days' advance notice of any price increases for existing
            paid subscribers. Continued use after the effective date of a price change constitutes
            acceptance of the new pricing.
          </p>

          {/* ── Disclaimers ── */}
          <SectionHeader id="disclaimers" title="10. Disclaimers & Warranties" />
          <p className="mb-4 text-muted-foreground">
            The Service is provided "as is" and "as available" without warranties of any kind,
            either express or implied, to the maximum extent permitted by applicable law.
          </p>
          <p className="mb-4 text-muted-foreground">
            We do not warrant that:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>The Service will be uninterrupted, error-free, or completely secure at all times.</li>
            <li>Any errors or defects will be corrected.</li>
            <li>The Service is appropriate for use in every jurisdiction.</li>
            <li>Any information or reports generated by the Service constitute legal, financial, tax, or professional advice. You should seek qualified professional advice for legal and financial matters.</li>
          </ul>
          <InfoBox>
            <strong>Not professional advice:</strong> {APP_NAME} is a data management tool.
            Nothing in the Service constitutes legal advice, tax advice, accounting advice,
            or any other professional advice. Always consult qualified professionals for
            landlord obligations, tax compliance, and tenancy law in your jurisdiction.
          </InfoBox>
          <p className="text-muted-foreground">
            Nothing in these Terms excludes or limits our liability for death or personal injury
            caused by our negligence, for fraud or fraudulent misrepresentation, or for any
            other liability that cannot be excluded under applicable EU law.
          </p>

          {/* ── Limitation of Liability ── */}
          <SectionHeader id="liability" title="11. Limitation of Liability" />
          <p className="mb-4 text-muted-foreground">
            To the maximum extent permitted by applicable law, {COMPANY_LEGAL_NAME} shall not
            be liable for:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business, or goodwill.</li>
            <li>Damages arising from your reliance on any data, reports, or information provided through the Service for legal, financial, or professional decisions.</li>
            <li>Any unauthorised access to or alteration of your data resulting from circumstances beyond our reasonable control.</li>
            <li>Any interruption or cessation of the Service.</li>
            <li>Losses arising from your failure to maintain adequate data backups or from data you choose to delete.</li>
          </ul>
          <p className="mb-4 text-muted-foreground">
            Where we are legally held liable, our total aggregate liability to you for all claims arising from or
            in connection with the Service shall be strictly limited to the total amount paid by you to us for your subscription in the 1 month preceding the claim, up to a maximum of €50 (fifty Euros) or $50 (fifty US Dollars).
          </p>
          <p className="mb-4 text-muted-foreground">
            If you are using the Service on a free plan, you acknowledge and agree that we shall have no financial liability to you whatsoever, and you are not eligible for any refund, compensation, or damages under any circumstances.
          </p>
          <p className="text-muted-foreground">
            Nothing in this Section limits our liability for fraud, wilful misconduct, gross
            negligence, death or personal injury, or any non-excludable statutory rights you
            have as a consumer under applicable EU law.
          </p>

          {/* ── Indemnification ── */}
          <SectionHeader id="indemnification" title="12. Indemnification" />
          <p className="mb-4 text-muted-foreground">
            You agree to defend, indemnify, and hold harmless {COMPANY_LEGAL_NAME}, its
            officers, directors, employees, and agents from and against any claims, damages,
            obligations, losses, liabilities, costs, or debts arising from:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Your use of or access to the Service in breach of these Terms.</li>
            <li>Your violation of any applicable law or regulation, including landlord-tenant law and data protection law.</li>
            <li>Your breach of any obligation owed to third parties, including your tenants, in connection with your use of the Service.</li>
            <li>Any content you upload to the Service that infringes third-party rights.</li>
          </ul>

          {/* ── Termination ── */}
          <SectionHeader id="termination" title="13. Termination" />
          <h3 className="font-semibold text-foreground mb-2">13.1 Termination by You</h3>
          <p className="mb-4 text-muted-foreground">
            You may terminate your account at any time by contacting us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>{" "}
            or by using the account deletion feature in Settings. Upon deletion, your data will
            be permanently erased in accordance with our{" "}
            <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>{" "}
            (excluding legally required retention of financial records).
          </p>

          <h3 className="font-semibold text-foreground mb-2">13.2 Termination by Us</h3>
          <p className="mb-4 text-muted-foreground">
            We reserve the right to suspend or terminate your account and access to the Service at
            any time and for any reason, with or without notice. This includes, but is not limited
            to, situations where we reasonably believe you have:
          </p>
          <ul className="list-disc pl-5 space-y-1 mb-4 text-muted-foreground">
            <li>Materially breached these Terms or our Acceptable Use Policy.</li>
            <li>Engaged in fraudulent, illegal, or harmful activity using the Service.</li>
            <li>Failed to pay any amounts due under a paid subscription.</li>
          </ul>

          <h3 className="font-semibold text-foreground mb-2">13.3 Effect of Termination</h3>
          <p className="mb-4 text-muted-foreground">
            Upon termination, your right to access and use the Service ceases immediately. We
            will provide you with a reasonable opportunity (30 days) to export your data before
            deletion, unless termination is for cause involving illegal activity. Sections of
            these Terms that by their nature should survive termination (including Sections 7.4,
            8, 11, 12, 14, and 15) will continue to apply.
          </p>

          {/* ── Governing Law ── */}
          <SectionHeader id="governing-law" title="14. Governing Law" />
          <p className="mb-4 text-muted-foreground">
            These Terms shall be governed by and construed in accordance with the laws of{" "}
            {GOVERNING_LAW_COUNTRY}, without regard to its conflict of law provisions.
          </p>
          <p className="mb-4 text-muted-foreground">
            If you are a consumer resident in an EU member state, you also benefit from any
            mandatory consumer protection laws in your country of residence that cannot be
            overridden by contract. Nothing in these Terms limits your statutory consumer rights
            under EU law.
          </p>
          <p className="text-muted-foreground">
            The courts of {GOVERNING_LAW_COUNTRY} shall have exclusive jurisdiction to settle
            any dispute arising from or in connection with these Terms, except where mandatory
            EU law requires otherwise (e.g. where you as a consumer may bring proceedings in
            the courts of your own member state).
          </p>

          {/* ── Disputes ── */}
          <SectionHeader id="disputes" title="15. Dispute Resolution" />
          <h3 className="font-semibold text-foreground mb-2">15.1 Informal Resolution</h3>
          <p className="mb-4 text-muted-foreground">
            Before initiating formal proceedings, we ask that you first contact us at{" "}
            <a href={`mailto:${LEGAL_EMAIL}`} className="text-primary hover:underline">{LEGAL_EMAIL}</a>{" "}
            to attempt to resolve the dispute informally. We will endeavour to resolve most
            disputes within 30 days of receiving your complaint.
          </p>

          <h3 className="font-semibold text-foreground mb-2">15.2 EU Online Dispute Resolution</h3>
          <p className="mb-4 text-muted-foreground">
            If you are an EU consumer, you may use the European Commission's Online Dispute
            Resolution (ODR) platform, which is designed to facilitate out-of-court resolution
            of disputes:
          </p>
          <div className="bg-muted/40 rounded-xl p-3 border border-border/40 text-sm mb-4">
            <a
              href="https://ec.europa.eu/consumers/odr"
              className="text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://ec.europa.eu/consumers/odr
            </a>
          </div>
          <p className="text-muted-foreground">
            Our contact email for ODR purposes is:{" "}
            <a href={`mailto:${LEGAL_EMAIL}`} className="text-primary hover:underline">{LEGAL_EMAIL}</a>
          </p>

          {/* ── Changes ── */}
          <SectionHeader id="changes" title="16. Changes to These Terms" />
          <p className="mb-3 text-muted-foreground">
            We may update these Terms from time to time. When we make changes, we will:
          </p>
          <ul className="list-disc pl-5 space-y-2 mb-4 text-muted-foreground">
            <li>Update the "Last updated" date at the top of this page.</li>
            <li>Provide reasonable notice before material changes take effect for registered users.</li>
            <li>For significant changes that affect your rights, provide a clear summary of what has changed.</li>
          </ul>
          <p className="text-muted-foreground">
            Your continued use of the Service after the effective date of revised Terms
            constitutes your acceptance of the changes. If you do not agree with the changes,
            you must stop using the Service and may request account deletion before the changes
            take effect.
          </p>

          {/* ── General ── */}
          <h2 className="scroll-mt-24 text-xl font-bold text-foreground mt-12 mb-4 pb-2 border-b border-border/60">
            17. General Provisions
          </h2>
          <h3 className="font-semibold text-foreground mb-2">17.1 Entire Agreement</h3>
          <p className="mb-4 text-muted-foreground">
            These Terms, together with our Privacy Policy, constitute the entire agreement
            between you and {COMPANY_LEGAL_NAME} with respect to the Service and supersede all
            prior agreements, representations, and understandings.
          </p>

          <h3 className="font-semibold text-foreground mb-2">17.2 Severability</h3>
          <p className="mb-4 text-muted-foreground">
            If any provision of these Terms is found to be unenforceable by a court of competent
            jurisdiction, that provision will be modified to the minimum extent necessary to make
            it enforceable, and the remaining provisions will continue in full force and effect.
          </p>

          <h3 className="font-semibold text-foreground mb-2">17.3 Waiver</h3>
          <p className="mb-4 text-muted-foreground">
            Our failure to enforce any right or provision of these Terms shall not be considered
            a waiver of those rights. A waiver of any breach shall not be construed as a waiver
            of any future or other breach.
          </p>

          <h3 className="font-semibold text-foreground mb-2">17.4 Assignment</h3>
          <p className="mb-4 text-muted-foreground">
            You may not assign or transfer these Terms or any rights or obligations hereunder
            without our prior written consent. We may assign these Terms in connection with a
            merger, acquisition, or sale of all or substantially all of our assets, with
            reasonable notice to you.
          </p>

          <h3 className="font-semibold text-foreground mb-2">17.5 Force Majeure</h3>
          <p className="mb-4 text-muted-foreground">
            We shall not be liable for any delay or failure in performance resulting from causes
            beyond our reasonable control, including acts of God, governmental actions, natural
            disasters, pandemic events, power failures, internet service disruptions, or acts of
            third parties.
          </p>

          <h3 className="font-semibold text-foreground mb-2">17.6 Language</h3>
          <p className="mb-4 text-muted-foreground">
            These Terms are written in English. In the event of any conflict between an English
            version and any translation, the English version shall prevail.
          </p>

          {/* ── Contact ── */}
          <SectionHeader id="contact" title="18. Contact Us" />
          <p className="mb-4 text-muted-foreground">
            If you have any questions, concerns, or feedback about these Terms, please contact us:
          </p>
          <div className="bg-muted/40 rounded-xl p-4 border border-border/40 text-sm mb-6 space-y-1">
            <p><strong>General enquiries:</strong>{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>
            </p>
            <p><strong>Legal / compliance:</strong>{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-primary hover:underline">{LEGAL_EMAIL}</a>
            </p>
          </div>
          <p className="text-muted-foreground">
            We aim to respond to all enquiries within 5 business days.
          </p>

          {/* Bottom cross-links */}
          <div className="mt-14 pt-8 border-t border-border/60 flex flex-col sm:flex-row gap-4">
            <Link
              to="/privacy-policy"
              className="flex-1 bg-muted/40 hover:bg-muted/70 border border-border/40 rounded-xl p-4 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Also read</p>
              <p className="font-semibold text-foreground text-sm">Privacy Policy →</p>
            </Link>
            <Link
              to="/"
              className="flex-1 bg-muted/40 hover:bg-muted/70 border border-border/40 rounded-xl p-4 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Back to</p>
              <p className="font-semibold text-foreground text-sm">{APP_NAME} Home →</p>
            </Link>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-muted/20 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-foreground transition-colors font-medium text-primary">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
